/**
 * Canonical inbound-protocol vocabulary (ADR-0253 §A — data integrity, 2026-10-23).
 *
 * ONE vocabulary, shared by every audit write path (edge draft, origin
 * dispatch, UCP origin draft, mandate pipeline, webhook ingress) and every
 * read surface (dashboard list, record detail, group panel, overview card,
 * `/v1/sites/:site_id/protocol-mix`). Previously each surface rolled its
 * own union (`'embed' | 'mcp' | 'a2a'`, `'embed' | 'mcp' | 'a2a' | 'ap2'`,
 * untyped strings on the audit wire); the result was ~18k rows/week on a
 * single site with `inbound_protocol = NULL` for ANY origin-dispatched
 * record (custom-webhook / connector), including A2A ingress. This file
 * is the single source of truth.
 *
 * Values (closed set, snake_case, wire-safe):
 *   - `'web'`  — browser-ish ingress on the merchant's own domain: the
 *                script tag, the WP plugin or the CF Worker forwarded a
 *                crawl event, or an HTML crawler hit the site. Writers:
 *                `process-draft.ts` on `surface:'embed'`, worker/plugin
 *                forward paths (`via` ∈ worker|plugin|server). Formerly
 *                written as `'embed'`; the derivation below folds the
 *                legacy value to `'web'` at read time.
 *   - `'mcp'`  — a per-site MCP `tools/call` (or a Phase-1 sentinel for
 *                an unwired tool). Writers: `process-draft.ts` on
 *                `surface:'mcp'`, `process-connector-draft.ts` on the
 *                origin-dispatched custom-webhook or connector call.
 *   - `'a2a'`  — an A2A SendMessage dispatched through the shared MCP
 *                dispatcher. Carries `a2a_task_id`. Writers:
 *                `process-draft.ts` on `surface:'a2a'`, origin dispatch
 *                when the edge forwards the A2A marker header.
 *   - `'ap2'`  — a signed AP2 mandate reached the transactional pipeline
 *                (`protocol_context.protocol === 'ap2'`).
 *   - `'ucp'`  — the UCP `complete` step composed a draft (every UCP
 *                checkout settles through this protocol).
 *   - `'acp'`  — an ACP SPT mandate reached the transactional pipeline
 *                (`protocol_context.protocol === 'acp'`).
 *   - `'pap'`  — a Personal Agent Protocol session-bound MCP call
 *                (ADR-0252). The `pap_session_id` continues to live
 *                UNSIGNED in `discovery_context.identification_input.mcp
 *                .pap_session_id` (same posture as the A2A task id), and
 *                every write path that sees a verified PAP session binding
 *                now writes `inbound_protocol = 'pap'` on the audit row's
 *                column. Writers: `process-draft.ts` on `surface:'pap'`,
 *                `process-connector-draft.ts` on the origin-dispatched
 *                custom-webhook or connector call with the trusted PAP
 *                marker forwarded by the edge. The derivation ladder below
 *                keeps `pap_session_id` as a legacy fallback for pre-column
 *                rows.
 *   - `'unknown'` — nothing above matched. Historical rows that fail
 *                 every derivation fall here so a merchant filter never
 *                 silently drops them.
 *
 * This file is PURE: no imports from siblings beyond the ambient audit
 * record shape. Both the API (origin runtime) and the dashboard
 * (browser) read it; adding runtime dependencies would break one of
 * them.
 */
/** The closed vocabulary. */
export const INBOUND_PROTOCOLS = [
    'web',
    'mcp',
    'a2a',
    'ap2',
    'ucp',
    'acp',
    'pap',
    'unknown',
];
/** Admitted values a WRITE path may emit directly (never `'unknown'`:
 *  that is a read-time fallback only). */
export const WRITABLE_INBOUND_PROTOCOLS = new Set([
    'web',
    'mcp',
    'a2a',
    'ap2',
    'ucp',
    'acp',
    'pap',
]);
export function isInboundProtocol(v) {
    return typeof v === 'string' && INBOUND_PROTOCOLS.includes(v);
}
/**
 * Fold a raw stored protocol value to the canonical vocabulary. Reads:
 *   - the `audit_record.inbound_protocol` column written on new rows;
 *   - the legacy `discovery_context.identification_input.surface` value
 *     the edge wrote before the column shipped (`'embed' | 'mcp' | 'a2a'`);
 *   - the signed `mandate.signed.protocol_context.protocol` value, when
 *     the row is transactional and the column stayed NULL (older rows).
 *
 * Priority is "most specific wins": the column + mandate protocol beat
 * the surface literal, which beats historical NULL rows.
 */
export function foldInboundProtocol(raw) {
    if (typeof raw !== 'string' || raw.length === 0)
        return 'unknown';
    const v = raw.toLowerCase();
    // Legacy `'embed'` (ADR-0211 wire label for script-tag / plugin / worker
    // ingress) is folded to `'web'`: the vocabulary surfaces the user-facing
    // word, not the internal implementation label.
    if (v === 'embed')
        return 'web';
    if (isInboundProtocol(v))
        return v;
    return 'unknown';
}
/**
 * Derive the canonical inbound protocol for a record. Order of precedence:
 *   1. The row's own `inbound_protocol` column (new rows + UCP + mandate).
 *   2. The signed mandate's `protocol_context.protocol` (ap2 / acp / ucp /
 *      mcp / a2a) when the column stayed NULL (historical transactional).
 *   3. The row's `discovery_context.identification_input.surface` literal
 *      (`embed | mcp | a2a`) — the edge wire that pre-dated the column.
 *   4. A UCP session id anywhere on the row → `'ucp'` (the pre-column UCP
 *      drafts carried `ucp_session_id` but no column value).
 *   5. A PAP session id → `'pap'`.
 *   6. `'unknown'`.
 *
 * The order means: if a row has `inbound_protocol = 'pap'` (new writes
 * from a verified PAP binding), it reads as `'pap'`. Pre-column rows that
 * carried `pap_session_id` under the previous "pap ⊂ mcp" convention
 * still fall through to step 5 and read as `'pap'`.
 */
export function deriveInboundProtocol(input) {
    const column = foldInboundProtocol(input.inbound_protocol);
    if (column !== 'unknown')
        return column;
    const ctx = foldInboundProtocol(input.protocol_context_protocol);
    if (ctx !== 'unknown')
        return ctx;
    const surface = foldInboundProtocol(input.discovery_context_surface);
    if (surface !== 'unknown')
        return surface;
    if (typeof input.ucp_session_id === 'string' && input.ucp_session_id.length > 0) {
        return 'ucp';
    }
    if (typeof input.pap_session_id === 'string' && input.pap_session_id.length > 0) {
        return 'pap';
    }
    return 'unknown';
}
/** Human-facing label used in list filters, chips and overview cards. */
export const INBOUND_PROTOCOL_LABEL = {
    web: 'Web (HTTP)',
    mcp: 'MCP',
    a2a: 'A2A',
    ap2: 'AP2',
    ucp: 'UCP',
    acp: 'ACP',
    pap: 'PAP',
    unknown: 'Unknown',
};
/**
 * SQL expression that reproduces `deriveInboundProtocol` on the server
 * (postgres-side). ONE source of truth: any route that joins
 * `audit_record` and projects a protocol uses this expression. Returns a
 * value in {@link INBOUND_PROTOCOLS} (never NULL). `table_alias` lets the
 * caller join `audit_record` under a non-default alias.
 *
 * NOTE: this returns the SQL FRAGMENT as a plain string. Postgres.js's
 * tagged-template evaluator substitutes `${sql(fragment)}` as raw SQL via
 * `sql.unsafe(...)` in callers; we DO NOT inline table values here. The
 * fragment uses `LOWER(COALESCE(...))` so `'embed'` folds to `'web'`
 * uniformly across list/single/aggregation queries.
 */
export function inboundProtocolSqlExpression(table_alias = 'r') {
    const a = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(table_alias) ? table_alias : 'r';
    return `
    CASE LOWER(COALESCE(
      ${a}.inbound_protocol,
      ${a}.discovery_context->'identification_input'->>'surface'
    ))
      WHEN 'embed' THEN 'web'
      WHEN 'web'   THEN 'web'
      WHEN 'mcp'   THEN 'mcp'
      WHEN 'a2a'   THEN 'a2a'
      WHEN 'ap2'   THEN 'ap2'
      WHEN 'ucp'   THEN 'ucp'
      WHEN 'acp'   THEN 'acp'
      WHEN 'pap'   THEN 'pap'
      ELSE 'unknown'
    END
  `;
}
//# sourceMappingURL=inbound-protocol.js.map